import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { JOURNEY_STAGES, fmtStagePct } from '../../lib/journeyStages';

/**
 * JourneyHeroStats — the Navigation Readiness hero.
 *
 * The four journey tiles (Capability → Visibility → Confidence →
 * Coordination) trace capability through the Missing Middle; the
 * Navigation Readiness tile on the far right — separated by a
 * divider — is the *value* of that journey (PE composite).
 */
export function JourneyHeroStats({ current, delta = {}, onStageClick }) {
  if (!current) return null;

  const fmtDelta = (metric) => {
    const d = delta?.[metric];
    if (d == null || d === 0) return null;
    const sign = d > 0 ? '+' : '−';
    return `${sign}${Math.abs(Math.round(d * 100))}pp MoM`;
  };

  const journey = JOURNEY_STAGES.filter((s) => s.key !== 'navigation').map((s) => ({
    ...s,
    value: current[s.metric],
  }));
  const lowest = journey.reduce(
    (acc, t) => (t.value != null && (acc == null || t.value < acc.value) ? t : acc),
    null,
  );

  return (
    <div className="mt-6 flex flex-col lg:flex-row gap-3 items-stretch" data-testid="pe-diag-hero-stats">
      {/* The journey */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
        {journey.map((s) => {
          const isLowest = lowest && s.key === lowest.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onStageClick?.(s.tab)}
              data-testid={`pe-diag-stat-${s.metric}`}
              title={`${s.blurb} — open the ${s.label} deep-dive`}
              className={cn(
                'group text-left rounded-xl bg-white px-4 py-3 border border-slate-200 ring-1 ring-slate-100 shadow-sm',
                'transition-all hover:bg-slate-50 hover:ring-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#44b6d2]/30',
                isLowest && 'ring-2 ring-amber-300 ring-offset-2 ring-offset-white animate-pulse-slow border-amber-200',
              )}
            >
              <p className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                <span className="truncate">{s.label}</span>
                {isLowest && (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-300/95 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900"
                    data-testid={`pe-diag-stat-${s.metric}-lowest-badge`}
                    title="Lowest journey stage this period — your primary constraint"
                  >
                    <AlertTriangle className="h-2.5 w-2.5" /> Gap
                  </span>
                )}
              </p>
              <p className="text-3xl font-extrabold text-slate-900 tabular-nums leading-tight mt-0.5">
                {fmtStagePct(s.value)}
              </p>
              <p className="text-[10.5px] mt-0.5 text-slate-500">{fmtDelta(s.metric) || s.blurb}</p>
              <p className="text-[10px] mt-0.5 text-slate-400">via {s.driver}</p>
            </button>
          );
        })}
      </div>

      {/* Divider — the journey ends, its value begins */}
      <div
        className="hidden lg:flex flex-col items-center justify-center px-1"
        aria-hidden="true"
        data-testid="journey-value-divider"
      >
        <div className="w-px flex-1 bg-slate-300/80" />
        <span className="py-1 text-[9px] font-bold uppercase tracking-widest text-slate-400 [writing-mode:vertical-rl]">
          value
        </span>
        <div className="w-px flex-1 bg-slate-300/80" />
      </div>
      <div className="lg:hidden h-px bg-slate-300/80" aria-hidden="true" />

      {/* Navigation Readiness — the value of the journey */}
      <div
        className="rounded-xl bg-slate-900 px-5 py-3 border border-slate-800 ring-1 ring-slate-700 shadow-md lg:w-60 flex flex-col justify-center"
        data-testid="pe-diag-stat-pe"
        data-metric-tile-headline="true"
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
          Navigation Readiness
        </p>
        <p className="text-4xl font-extrabold text-white tabular-nums leading-tight mt-0.5">
          {fmtStagePct(current.pe)}
        </p>
        <p className="text-[10.5px] mt-0.5 text-slate-300">
          {fmtDelta('pe') || 'Readiness to convert capability into business value'}
        </p>
      </div>
    </div>
  );
}
